import React, { useEffect, useState } from 'react';
import { SortableTree, SimpleTreeItemWrapper } from 'dnd-kit-sortable-tree';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-responsive-modal';
import { useFormik } from 'formik';
import { Form, Col } from 'react-bootstrap';
import { FaTrash } from "react-icons/fa";

import { getConstructorCriterions, getSpecializations, updadeCriterions } from '../../actions/common'
import Loader from '../../components/Loader';
import Button from '../../components/Button';
import GoBack from '../../components/GoBack';
import Confirmation from '../../components/Confirmation/Confirmation'

import { updateObjectById, insertAfterById, insertBeforeById, insertChildById, appendAtTopLevel, removeById } from '../../utils/constructor'
import { getDndCriterions, getModifyCriterions } from '../../utils/smallHelpers'
import { getOptions } from '../../utils/getOptions';

import styles from './styles.module.scss'
import { newCriterionValidationSchema } from '../../assets/validation';


const TreeItem = React.forwardRef(({ item, onEdit, onDelete, NEW_ITEM, ...props }, ref) => (
  <SimpleTreeItemWrapper {...props} ref={ref} className={styles.treeItem}>
    <div>
      <div>{item.value}</div>
      <div>
        <button title='Добавить ниже' onClick={onEdit('insertAfter', NEW_ITEM, item.id)}>↓</button>
        <button title='Добавить выше' onClick={onEdit('insertBefore', NEW_ITEM, item.id)}>↑</button>
        <button title='Добавить дочерний' onClick={onEdit('insertChild', NEW_ITEM, item.id)}>↳</button>
        <button title='Редактировать' onClick={onEdit('edit', item)}>✎</button>
        <button title='Удалить' onClick={onDelete(item.id)}><FaTrash size={16}/></button>
      </div>
    </div>
  </SimpleTreeItemWrapper>
));


function Constructor () {
  
  const NEW_ITEM = {
    id: Date.now(),
    value: '',
    points: '',
    documents: '',
    description: ''
  }

  const dispatch = useDispatch()
  const { specializations, criterions, loading: updateLoading } = useSelector(state => state.common)

  const [items, setItems] = useState([]);
  const [position, setPosition] = useState('');
  const [loading, setLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [parentId, setParentId] = useState(null)
  const [modalMode, setModalMode] = useState('')
  const [confirmation, setConfirmation] = useState('')


  useEffect(() => {
    !specializations && dispatch(getSpecializations())
  }, [])
  
  useEffect(() => {
    getCriterionsAction()
  }, [position])
  

  useEffect(() => {
    setItems(getDndCriterions(criterions))
  }, [criterions])
  
  const getCriterionsAction = async () => {
    if (!position) return;
    setLoading(true)
    await dispatch(getConstructorCriterions(position))
    setLoading(false)
  }
  
  const getUpdateCB = () => {
    if (modalMode === 'insertAfter') return insertAfterById
    if (modalMode === 'insertBefore') return insertBeforeById
    if (modalMode === 'insertChild') return insertChildById
    if (modalMode === 'edit') return updateObjectById
    return appendAtTopLevel
  }

  const updateCriterionHandler = (item) => {
    const updateCB = getUpdateCB()
    const initId = modalMode === 'edit' ? selectedItem.id : parentId
    const newItems = updateCB(items, item, initId)
    setItems(newItems)
    setSelectedItem(null)
    closeModal()
  }

  const handleSubmit = async (withApprove) => {
    const payload = getModifyCriterions(items)
    const {error, message} = await dispatch(updadeCriterions(position, payload, !!withApprove))
    error === 'Конфликт критериев' && setConfirmation(message)
  }  
  
  const acceptConfirmation = () => {
    handleSubmit(true)
    setConfirmation('')
  }

  const deleteItem = (id) => (e) => {
    e.stopPropagation()
    const newItems = removeById(items, id)
    setItems(newItems)
  }

  const openModal = (mode, item, parentId) => () => {
    setModalMode(mode)
    setSelectedItem(item)
    setParentId(parentId)
  }
  
  const closeModal = () => {
    setModalMode('')
    setSelectedItem(null)
    setParentId(null)
  }

  const renderOption = ({id, name, school_type}) => {
    const music = school_type === 'musical'
    const common = school_type === 'common_school'
    const label = `${name}${music ? '*' : common ? '**' : ''}`
    return <option key={id} value={id}>{label}</option>
  }

  return (
    <div className={styles.wrapper}>
      <GoBack/>
      {confirmation &&
        <Confirmation
          title={`${confirmation}. Вы уверены что хотите внести изменения?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={() => setConfirmation('')}
        />
      }
      <h3>Конструктор критериев</h3>

      <section>
        <Form.Group className='mt-3' as={Col} md={4}>
          <Form.Label className={styles.formLabels}>Должность:</Form.Label>
          <Form.Select
            value={position}
            onChange={e => setPosition(e.target.value)}
          >
            <option value=''>Выбрать</option>
            {specializations?.map(renderOption)}
          </Form.Select>
        </Form.Group>

        <div>
          <div>* - музыкальная</div>
          <div>** - школы</div>
        </div>
      </section>

      <ConstructorModal
        open={!!selectedItem}
        selectedItem={selectedItem}
        closeModal={closeModal}
        modalMode={modalMode}
        onSubmit={updateCriterionHandler}
      />

      {loading && <Loader/>}
      {!loading &&
        <>
          <SortableTree
            items={items}
            onItemsChanged={setItems}
            TreeItemComponent={React.forwardRef((props, ref) =>
              <TreeItem
                {...props}
                ref={ref}
                NEW_ITEM={NEW_ITEM}
                onEdit={openModal}
                onDelete={deleteItem}
              />
            )}
          />
          { !loading && position && 
            <>
              <button title='Добавить критерий верхнего уровня' onClick={openModal('add', NEW_ITEM)}>+</button>
              <Button submitting={updateLoading}  onClick={() => handleSubmit()}>Сохранить</Button>
            </>  
          }
        </>
      }
    </div>
  );
}

export default Constructor;


const ConstructorModal = ({
  open,
  closeModal,
  selectedItem,
  modalMode,
  onSubmit
}) => {
  
  const modalTitle = modalMode === 'edit' ? 'Изменение критерия' : 'Добавление критерия'

  useEffect(() => {
    if (!open) return;
    setValues(selectedItem)
  }, [open])
  
  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true,
    validationSchema: newCriterionValidationSchema,
    onSubmit
  })
  
  const { values, errors, touched, submitCount, handleBlur, setValues, handleSubmit, handleChange } = formik

  const error = (fieldName) => (touched[fieldName] || !!submitCount) && errors[fieldName]

  return (
    <Modal
      open={open}
      onClose={closeModal}
      showCloseIcon={false}
      animationDuration={1}
      classNames={{modal: styles.modalWrapper}}
    > 
      <h3>{modalTitle}</h3>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Название</Form.Label>
          <Form.Control
            name='value'
            type="text"
            placeholder="Название"
            value={values.value}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={error('value')}
            />
          <Form.Control.Feedback type="invalid">{errors.value}</Form.Control.Feedback>
          
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Баллы</Form.Label>
          <Form.Control
            type="number"
            style={{ width: '80px' }}
            placeholder="Баллы"
            value={values.points}
            onChange={handleChange}
            name='points'
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Наличие подтверждающих документов в портфолио</Form.Label>
          <Form.Control
            as='textarea'
            rows={5}
            value={values.documents}
            onChange={handleChange}
            name='documents'
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Описание</Form.Label>
          <Form.Control
            as='textarea'
            rows={5}
            value={values.description}
            onChange={handleChange}
            name='description'
          />
        </Form.Group>
        <div>
          <Button type='submit'>Сохранить</Button>
          <Button type='button' onClick={closeModal}>Отменить</Button>
        </div>
      </Form>
    </Modal>
  )
}